/**
 * BondEngagementEditor — S4 PR-b (Corrente de Compromissos, Item 404 faceta b):
 * EDITAR a política de ATUAÇÃO da IA por-cliente direto na ficha da Pessoa
 * (molde literal `BondAutonomyEditor`).
 *
 * A política mora no `BondParticipant.aiEngagementMode` (override por-cliente).
 * `'no_engage'` marca que a IA nunca conduz este cliente (a conversa nasce/
 * segue H_a); `null` limpa o override (volta a herdar `'engage'`).
 *
 * `'welcome_only'` («IA só acolhe e passa») é RESERVADO — o motor ainda o trata
 * como `'engage'` (o entrypoint de escalação pós-acolhimento não é invocável
 * limpo de fora do conductor, design §3-bis) e o DTO do PATCH o REJEITA.
 * Oferecê-lo aqui seria affordance falsa (operador marca "só acolhe" e a IA
 * conduz normal) — por isso aparece DESABILITADO com nota "em breve", não
 * omitido (a UI é honesta sobre o que existe e o que está a caminho).
 *
 * Só renderiza onde a pessoa é participant (tem participantId/version p/ o
 * If-Match) — precedente `BondAutonomyEditor`.
 */
import { useState } from 'react';

import { useToastStore } from '../../stores/toast-store';

import { useUpdateBondEngagement } from './use-persons';

import type { JSX } from 'react';

const MODE_OPTIONS: { value: string; label: string; desc: string; disabled?: boolean }[] = [
  {
    value: '',
    label: 'IA atua normalmente (herda)',
    desc: 'Segue a configuração do playbook / tenant. Use quando o cliente não precisa de tratamento diferente.',
  },
  {
    value: 'no_engage',
    label: 'IA não atua com este cliente',
    desc: 'A conversa nasce (ou passa a seguir) com um humano no controle — a IA nunca conduz.',
  },
  {
    value: 'welcome_only',
    label: 'IA só acolhe e passa',
    desc: 'Em breve: a IA vai acolher o cliente e encaminhar a um humano no primeiro contato. Ainda não disponível.',
    disabled: true,
  },
];

export interface BondEngagementEditorProps {
  personId: string;
  bondId: string;
  participantId: string;
  version: number;
  currentMode: string | null;
}

export function BondEngagementEditor({
  personId,
  bondId,
  participantId,
  version,
  currentMode,
}: BondEngagementEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState(currentMode ?? '');
  const update = useUpdateBondEngagement(personId);
  const toast = useToastStore((s) => s.push);

  const open = (): void => {
    setMode(currentMode ?? '');
    setEditing(true);
  };

  const save = (): void => {
    if (update.isPending) return;
    update.mutate(
      {
        bondId,
        participantId,
        version,
        aiEngagementMode: mode === '' ? null : mode,
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast({
            variant: 'success',
            title: 'Política de atuação da IA atualizada',
            durationMs: 3000,
          });
        },
        onError: () => {
          toast({
            variant: 'error',
            title: 'Não foi possível atualizar a política.',
            description: 'A ficha pode ter sido alterada — recarregue e tente novamente.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-label="Editar política de atuação da IA"
        onClick={open}
        style={{ alignSelf: 'flex-start' }}
      >
        Editar política de IA
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-md"
      role="radiogroup"
      aria-label="Política de atuação da IA por cliente"
      style={{
        padding: 'var(--space-3)',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-default)',
        maxWidth: 520,
      }}
    >
      <div className="flex flex-col gap-2">
        {MODE_OPTIONS.map((o) => {
          const active = mode === o.value;
          const disabled = o.disabled === true;
          return (
            <button
              key={o.value === '' ? 'inherit' : o.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={o.label}
              aria-disabled={disabled}
              disabled={disabled}
              title={
                disabled ? 'Ainda não disponível — chega numa próxima atualização.' : undefined
              }
              onClick={() => {
                if (disabled) return;
                setMode(o.value);
              }}
              className="flex items-start gap-2 rounded-md text-left"
              style={{
                padding: 'var(--space-3)',
                border: `1px solid ${active ? 'var(--brand)' : 'var(--border-default)'}`,
                background: active ? 'var(--brand-subtle)' : 'var(--bg-surface)',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  width: 16,
                  height: 16,
                  marginTop: 2,
                  flex: 'none',
                  border: `2px solid ${active ? 'var(--brand)' : 'var(--border-strong)'}`,
                  background: active
                    ? 'radial-gradient(circle, var(--brand) 42%, transparent 46%)'
                    : 'transparent',
                }}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold" style={{ color: 'var(--fg-1)' }}>
                  {o.label}
                </span>
                <span
                  className="block text-xs"
                  style={{ color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.45 }}
                >
                  {o.desc}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={save}
          disabled={update.isPending}
        >
          {update.isPending ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setEditing(false)}
          disabled={update.isPending}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
